import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Plus, Check } from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../hooks/useToast";

interface Service {
  id: string;
  name: string;
  duration: string;
  price: string;
  staffIds?: number[];
}
interface Props {
  services: Service[];
  setSelectedService?: (service: Service) => void;
  setStep: (
    step: "selection" | "customer" | "summary" | "payment" | "finalPayment"
  ) => void;
  disabledServices?: Service[];
  isAddingService?: boolean;
  setIsAddingService?: (value: boolean) => void;
  setSelectedServices?: (services: Service[]) => void;
  selectedStylistId?: number;
}

const ServiceSelection: React.FC<Props> = ({
  services,
  setSelectedService,
  setStep,
  disabledServices = [],
  isAddingService = false,
  setIsAddingService,
  setSelectedServices,
  selectedStylistId,
}) => {
  const { user } = useAuth();
  const toast = useToast();

  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="flex flex-col gap-4">
      {/* List Container */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex flex-col gap-3"
      >
        {services.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-200">
            <p className="text-sm text-gray-400 font-medium">
              No services available for this stylist/slot.
            </p>
          </div>
        ) : (
          services.map((service) => {
            const isDisabled = disabledServices.some((s) => s.id === service.id);

            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className={cn(
                  "group relative flex items-center justify-between p-5 bg-white border border-gray-100 rounded-xl cursor-pointer transition-all duration-300 shadow-sm",
                  isDisabled ? "opacity-40 cursor-not-allowed grayscale" : ""
                )}
                onClick={() => {
                  if (isDisabled) return;

                  if (
                    isAddingService &&
                    setSelectedServices &&
                    setIsAddingService
                  ) {
                    setSelectedServices([...disabledServices, service]);
                    setStep("summary");
                    setIsAddingService(false);
                  } else if (setSelectedServices) {
                    setSelectedServices([service]);
                    setStep("customer");
                  } else if (setSelectedService) {
                    setSelectedService(service);
                    setStep("customer");
                  }
                }}
              >
                <div className="flex-1 min-w-0 pr-4">
                  <h3 className="text-base font-[600] text-[#885ABB] truncate" title={service.name}>
                    {service.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 text-gray-400">
                    <Clock size={12} />
                    <span className="text-xs font-medium">{service.duration}</span>
                  </div>
                </div>

                <div className="text-right flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    {isDisabled && (
                      <div className="flex items-center gap-1 opacity-70">
                        <Check size={10} className="text-[#885ABB]" strokeWidth={4} />
                        <span className="text-[9px] font-[600] text-[#885ABB] uppercase">Selected</span>
                      </div>
                    )}
                    {selectedStylistId && service.staffIds && !service.staffIds.includes(selectedStylistId) && (
                      <div className="bg-[#885ABB]/10 text-[#885ABB] text-[8px] px-2 py-0.5 rounded-full font-bold uppercase mb-1 whitespace-nowrap">
                        Not provided by stylist
                      </div>
                    )}
                    <span className="text-lg font-[600] text-[#885ABB]">
                      {currencySymbol}{service.price}
                    </span>
                  </div>
                </div>

                {/* Left Indicator bar - Permanent design element */}
                <div className={cn(
                  "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full transition-colors duration-300",
                  isDisabled ? "bg-gray-300" : "bg-[#885ABB]"
                )} />
              </motion.div>
            );
          })
        )}
      </motion.div>
    </div>
  );
};

export default ServiceSelection;
